import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import portals from "./portals";
import deadbird from "../resources/icons/feather-pointed 1.svg";

const BrandCounter = ({ counters = [], isSleeping = false }) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(411);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const safeCounters = Array.isArray(counters) ? counters : [];
  const filteredCounters = safeCounters.filter(counter => counter.count > 0);
  const sortedCounters = [...filteredCounters].sort((a, b) => b.count - a.count);
  const allCountsAreZero = sortedCounters.length === 0;

  const totalWidth = containerWidth - 64;
  const gap = 4;
  const minWidth = 64;
  const totalCount = sortedCounters.reduce((acc, counter) => acc + counter.count, 0);
  const availableWidth = totalWidth - gap * (sortedCounters.length - 1);

  if (allCountsAreZero) {
    return (
      <div className="brand-counter-container" ref={containerRef}>
        <div
          className="brand-counter"
          style={{
            width: totalWidth,
            backgroundColor: isSleeping ? "rgba(255,255,255,0.08)" : "rgba(17,17,17,0.10)",
            color: isSleeping ? "rgba(255,255,255,0.4)" : "black",
            pointerEvents: "none",
          }}
        >
          <img
            src={deadbird}
            alt="No data yet"
            className="brand-logo smooth-svg"
            style={{ filter: isSleeping ? "invert(100%)" : "invert(0%)" }}
          />
          <span className="order-count">No data yet</span>
        </div>
      </div>
    );
  }

  let widths = sortedCounters.map((counter) => {
    const proportion = counter.count / totalCount;
    return proportion * availableWidth;
  });

  let remainingWidth = availableWidth;
  widths = widths.map((width) => {
    const adjustedWidth = Math.max(minWidth, width);
    remainingWidth -= adjustedWidth;
    return adjustedWidth;
  });

  const totalCalculatedWidth = widths.reduce((acc, width) => acc + width, 0);
  if (totalCalculatedWidth > availableWidth) {
    const reductionFactor = availableWidth / totalCalculatedWidth;
    widths = widths.map((width) => width * reductionFactor);
  }

  return (
    <div className="brand-counter-container" ref={containerRef}>
      {sortedCounters.map((counter, index) => {
        const portal = Object.values(portals).find(p => p.display === counter.portal) || { display: "Unknown", logo: null };
        const portalLogo = portal.logo;
        const portalName = portal.name;
        const isLeader = counter.count === sortedCounters[0].count;

        // Kleuren per modus
        const bgColor = isSleeping
          ? isLeader ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.08)"
          : isLeader ? "black" : "rgba(17,17,17,0.10)";

        const textColor = isSleeping
          ? isLeader ? "#111" : "rgba(255,255,255,0.6)"
          : isLeader ? "white" : "black";

        const logoFilter = isSleeping
          ? isLeader ? "invert(100%)" : "none"
          : isLeader ? "none" : "invert(100%)";

        return (
          <div
            key={index}
            className="brand-counter"
            style={{
              width: `${widths[index]}px`,
              backgroundColor: bgColor,
              color: textColor,
              transition: "width 0.5s ease-in-out, background-color 0.5s ease-in-out",
            }}
          >
            {portalLogo && (
              <img
                src={portalLogo}
                alt={`${portalName} logo`}
                className="brand-logo smooth-svg"
                style={{ filter: logoFilter }}
              />
            )}
            <span className="order-count">{counter.count}</span>
          </div>
        );
      })}
    </div>
  );
};

BrandCounter.propTypes = {
  counters: PropTypes.arrayOf(
    PropTypes.shape({
      portal: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    })
  ),
  isSleeping: PropTypes.bool,
};

BrandCounter.defaultProps = {
  counters: [],
  isSleeping: false,
};

export default BrandCounter;